/**
 * ElevenLabs Service - Voice cloning and TTS (now routes through User Backend)
 * 
 * This service maintains backward compatibility with existing components
 * while routing all AI requests through the secure User Backend.
 * 
 * NO API KEYS IN FRONTEND - all generation goes through:
 * Frontend → User Backend → Admin Backend → ElevenLabs
 */

import * as aiService from './aiService';

// Legacy storage key - kept for backward compatibility but no longer used
export const ELEVENLABS_API_KEY_STORAGE_KEY = 'loopai-moviex-elevenlabs-api-key-v2';

/**
 * Clone a voice using ElevenLabs (via secure backend)
 */
export const cloneVoiceWithElevenLabs = async (name, audioDataUri) => {
    const result = await aiService.cloneVoice(name, audioDataUri);
    return result.voiceId;
};

/**
 * Generate speech using ElevenLabs TTS (via secure backend).
 *
 * Returns a data URI ready to feed into <audio src="…"> or a Blob player.
 *
 * Handles:
 *   • base64 payloads with OR without the "data:audio/…;base64," prefix
 *   • signed https audio URLs (fetched via GET, then blob→data-URI)
 *   • CORS-restricted URLs (retries WITHOUT credentials on 401/CORS)
 *   • async job polling (handled upstream in aiService.generateSpeech)
 */
export const generateSpeechWithElevenLabs = async (text, voiceId) => {
    const result = await aiService.generateSpeech(text, voiceId);

    // ── 1) Base64 first (no round-trip needed) ──
    if (result.audioData) {
        const b64 = String(result.audioData).trim();
        if (b64.startsWith('data:')) return b64;
        // Guess mime from ElevenLabs default (mp3) — most players auto-sniff.
        return `data:audio/mpeg;base64,${b64}`;
    }

    // ── 2) URL — fetch → blob → data URI ──
    if (result.audioUrl) {
        const url = result.audioUrl;
        const toDataUri = (blob) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
        // First try WITH credentials (matches our internal endpoints).
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return await toDataUri(await response.blob());
        } catch (e) {
            // CORS / 401 → retry WITHOUT credentials (public signed URL).
            try {
                const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return await toDataUri(await response.blob());
            } catch (e2) {
                console.error('Failed to fetch audio URL:', url, e2);
                // Last resort — return the URL directly. The <audio> element
                // can still stream it even if we couldn't inline it.
                return url;
            }
        }
    }

    throw new Error('No audio data received');
};

/**
 * Get available voices from ElevenLabs (via secure backend)
 */
export const getElevenLabsVoices = async () => {
    return aiService.getVoices();
};

// ==================== DEPRECATED / NO-OP FUNCTIONS ====================

// These functions are no longer needed as API keys are managed server-side
export const getElevenLabsApiKey = () => {
    console.warn('API keys are now managed server-side.');
    return 'managed-server-side';
};
